// ─── Base fetch (réutilise le même pattern que lib/api.ts) ────────────────────

import { httpRequest } from './http.js'

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  return httpRequest<T>(path, options)
}

const get   = <T>(p: string)              => apiFetch<T>(p)
const post  = <T>(p: string, b?: unknown) => apiFetch<T>(p, { method: 'POST',   body: b ? JSON.stringify(b) : undefined })
const patch = <T>(p: string, b: unknown)  => apiFetch<T>(p, { method: 'PATCH',  body: JSON.stringify(b) })
const del   = <T>(p: string)              => apiFetch<T>(p, { method: 'DELETE' })

const qs = (p: Record<string, unknown>) => {
  const e = Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '')
  return e.length ? '?' + e.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&') : ''
}

export interface AdBlockGap {
  startTime: string
  endTime: string
  durationSec: number
}

export interface AdBlockInsertionSpace {
  infoservice: {
    itemId: string
    startTime: string
    endTime: string
    memberIds: string[]
  }
  shifts: Array<{
    id: string
    expectedStartTime: string
    startTime: string
  }>
}

export interface AdBlockRangeStat {
  key: string
  label: string
  gapCount: number
  totalDurationSec: number
  gaps: AdBlockGap[]
}

export interface AdBlockProposedSpot {
  spotId: string
  spotTitle: string
  campaignId: string
  campaignName: string
  advertiserName: string
  durationSec: number
  startTime: string
  airingsThisPeriod: number
  isFirstAiring: boolean
}

export interface AdBlockProposal {
  gap: AdBlockGap
  spots: AdBlockProposedSpot[]
  totalDurationSec: number
  remainingSec: number
  excludedCampaigns: Array<{ campaignId: string; campaignName: string; reason: string }>
}

export interface AdBlockWeekProposal {
  date: string
  gap: AdBlockGap
  proposal: AdBlockProposal | null
  reason?: 'NO_MATCHING_GAP'
}

export interface InfoserviceResolution {
  spot: {
    id: string
    title: string
    filePath: string | null
    durationSec: number
    active: boolean
    spotKind: string
    mediaDate: string | null
  }
  mediaDate: string | null
  fileName: string
  /** Présents uniquement après une nouvelle sélection de fichier. */
  updatedFutureItems?: number
  updatedGridIds?: string[]
}

export interface InfoserviceFillResult {
  gridId: string
  date: string
  gapCount: number
  created: number
  items: Array<{ id: string; startTime: string; endTime: string; durationSec: number }>
  resolution: InfoserviceResolution | null
}

/** Détail d'un bloc pub déjà placé en grille (édition via menu contextuel). */
export interface AdBlockDetail {
  id:          string
  gridId:      string
  startTime:   string
  endTime:     string
  durationSec: number
  status:      string
  isEditable:  boolean
  neighbours:  { before: AdBlockNeighbour | null; after: AdBlockNeighbour | null }
  spots: Array<{
    placementId:    string
    spotId:         string
    title:          string
    durationSec:    number
    filePath:       string | null
    campaignId:     string | null
    campaignName:   string | null
    advertiserName: string | null
    scheduledAt:    string
  }>
}

/** Plage d'infoservice touchant le bloc, exploitable pour un réajustement. */
export interface AdBlockNeighbour {
  seedId:    string
  memberIds: string[]
  startTime: string
  endTime:   string
  available: boolean
}

/** Côté que l'infoservice voisin absorbe quand la durée du bloc change. */
export type AdBlockRealignSide = 'BEFORE' | 'AFTER' | 'FILL'

export interface AdTransitionSettings {
  mode:             'BLACK' | 'VIDEO'
  blackDurationMs:  number
  videoFilePath:    string
  videoFileName:    string
  videoDurationMs:  number
  canEdit:          boolean
  blockedReason:    string | null
  blockedUntil:     string | null
  updatedBlocks?:   number
  gridIds?:         string[]
}

export type AlternatingAdsMode = 'ALTERNATE' | 'SINGLE'
export type AlternatingAdsPlacement = 'BEFORE' | 'AFTER'

export interface AlternatingAdsConfig {
  channelId: string
  gridId: string
  date: string
  spotIds: string[]
  firstSpotId: string
  mode?: AlternatingAdsMode
  placement?: AlternatingAdsPlacement
}

export type AlternatingAdsSkipReason =
  | 'INFOSERVICE_TOO_SHORT'
  | 'FROZEN'
  | 'CONFLICT'
  | 'SOURCE_MISSING'
  | 'ALREADY_PRESENT'
  | 'BLOCK_LOCKED'

export interface AlternatingAdsTarget {
  targetType: 'INSERTION' | 'EXISTING' | 'GAP' | 'APPEND'
  placement: AlternatingAdsPlacement
  targetKey: string
  date: string
  infoserviceStartTime: string
  infoserviceEndTime: string
  infoserviceDurationSec: number
  infoserviceTitle: string
  existingBlockId: string | null
  spot: { id: string; title: string; durationSec: number }
  startTime: string
  endTime: string
  possible: boolean
  reason: AlternatingAdsSkipReason | null
  /** Cochée d'office : faux pour un ajout à un bloc portant déjà un spot choisi. */
  recommended: boolean
}

export interface AlternatingAdsPreview {
  gridId: string
  date: string
  targets: AlternatingAdsTarget[]
  applicableCount: number
  existingCount: number
  ignoredCount: number
}

// ─── Advertisers ─────────────────────────────────────────────────────────────

export const advertisersApi = {
  list:   ()                                    => get<any[]>('/advertisers'),
  create: (data: { name: string; contactEmail?: string; notes?: string }) => post<any>('/advertisers', data),
  update: (id: string, data: Partial<any>)      => patch<any>(`/advertisers/${id}`, data),
  delete: (id: string)                          => del<void>(`/advertisers/${id}`),
}

// ─── Inventaire et tenue d'engagement ────────────────────────────────────────

export type PacingVerdict =
  | 'DONE'
  | 'NOT_STARTED'
  | 'ON_TRACK'
  | 'TIGHT'
  | 'UNREACHABLE'
  | 'ENDED_SHORT'

export interface CampaignPacing {
  campaignId:     string
  soldSpots:      number
  placedSpots:    number
  airedSpots:     number
  remainingSpots: number
  reachableSpots: number
  availableSec:   number
  daysRemaining:  number
  avgSpotSec:     number
  blocker:        'NO_USABLE_SPOT' | 'FREQUENCY' | 'INVENTORY' | null
  verdict:        PacingVerdict
}

export interface ChannelInventory {
  campaigns:        CampaignPacing[]
  demandSpots:      number
  capacitySpots:    number
  pressurePct:      number
  horizonEnd:       string | null
  horizonTruncated: boolean
}

// ─── Campaigns ───────────────────────────────────────────────────────────────

/** Un passage à venir d'une campagne, tel que le détail l'affiche. */
export interface CampaignUpcomingAiring {
  placementId: string
  scheduledAt: string
  spotId:      string
  spotTitle:   string
  durationSec: number
  itemId:      string
  itemType:    string
  itemStatus:  string
  itemStart:   string
  gridId:      string
}

/** Résultat d'une suppression définitive en lot depuis le filtre « Archivées ». */
export interface CampaignBulkDeleteResult {
  deleted: string[]
  /** Refusées faute d'être archivées — le catalogue courant n'est pas supprimable. */
  refused: string[]
  missing: string[]
}

export const campaignsApi = {
  /** Inventaire restant du canal : une lecture pour la liste et le détail. */
  pacing:  (channelId: string) => get<ChannelInventory>(`/ad-campaigns/pacing${qs({ channelId })}`),
  list:    (q: { channelId?: string; status?: string; archived?: 'active' | 'archived' | 'all' } = {}) => get<any[]>(`/ad-campaigns${qs(q)}`),
  getById: (id: string)                                       => get<any>(`/ad-campaigns/${id}`),
  create:  (data: any)                                        => post<any>('/ad-campaigns', data),
  update:  (id: string, data: Partial<any>)                   => patch<any>(`/ad-campaigns/${id}`, data),
  delete:  (id: string)                                       => del<void>(`/ad-campaigns/${id}`),
  archive: (id: string)                                       => post<any>(`/ad-campaigns/${id}/archive`),
  restore: (id: string)                                       => post<any>(`/ad-campaigns/${id}/restore`),
  /** Suppression définitive en lot ; refusée sur une campagne non archivée. */
  bulkDelete: (campaignIds: string[])                         => post<CampaignBulkDeleteResult>('/ad-campaigns/bulk-delete', { campaignIds }),
  upcoming: (id: string, limit = 4)                           => get<CampaignUpcomingAiring[]>(`/ad-campaigns/${id}/upcoming${qs({ limit })}`),
  autoPlace: (id: string)                                     => post<{ placed: number }>(`/ad-campaigns/${id}/auto-place`),
}

// ─── Spots ───────────────────────────────────────────────────────────────────

export const spotsApi = {
  // Spots d'une campagne
  listByCampaign: (campaignId: string) => get<any[]>(`/ad-campaigns/${campaignId}/spots`),
  createInCampaign: (campaignId: string, data: any) => post<any>(`/ad-campaigns/${campaignId}/spots`, data),

  // Bibliothèque de spots (standalone ou tous)
  list: (q: { channelId?: string; advertiserId?: string; standalone?: boolean; spotKind?: 'COMMERCIAL' | 'INFOSERVICE' } = {}) => {
    const p: Record<string, unknown> = { ...q }
    if (q.standalone !== undefined) p.standalone = q.standalone ? 'true' : 'false'
    return get<any[]>(`/ad-spots${qs(p)}`)
  },
  create:  (data: { channelId: string; advertiserId?: string; title: string; durationSec: number; filePath: string }) =>
    post<any>('/ad-spots', data),
  update:  (id: string, data: Partial<any>) => patch<any>(`/ad-spots/${id}`, data),
  delete:  (id: string)                     => del<void>(`/ad-spots/${id}`),

  /** Fiche technique du fichier réellement associé (présence + en-têtes ffprobe). */
  media: (id: string) => get<SpotMediaInfo>(`/ad-spots/${id}/media`),
  /** Prévisualise les blocs à venir recalés par le nouveau fichier, sans rien écrire. */
  previewReplaceMedia: (id: string, data: { filePath: string; durationSec: number }) =>
    post<SpotMediaReplacePreview>(`/ad-spots/${id}/replace-media`, { ...data, apply: false }),
  /** Remplace le fichier et recale les blocs à venir, dans une transaction unique. */
  applyReplaceMedia: (id: string, data: { filePath: string; durationSec: number }) =>
    post<SpotMediaReplaceResult>(`/ad-spots/${id}/replace-media`, { ...data, apply: true }),
}

export interface SpotMediaInfo {
  /** `DUPLICATE` : plusieurs fichiers portent ce nom, aucun n'est sûr. */
  status:        'OK' | 'MISSING' | 'NOT_SET' | 'ERROR' | 'DUPLICATE'
  fileName:      string | null
  filePath:      string | null
  codec:         string | null
  container:     string | null
  durationSec:   number | null
  resolution:    string | null
  fileSizeBytes: string | null
  issues:        string[]
}

export type SpotReplaceBlockerReason = 'ON_AIR' | 'NO_NEIGHBOUR' | 'INFOSERVICE_TOO_SHORT' | 'BUNDLE_LOCKED'

export interface SpotMediaReplacePreview {
  spotId:              string
  previousFilePath:    string | null
  previousDurationSec: number
  filePath:            string
  durationSec:         number
  blocks: Array<{
    itemId:       string
    startTime:    string
    endTime:      string
    newStartTime: string
    newEndTime:   string
    side:         'AFTER' | 'BEFORE' | null
    deltaSec:     number
  }>
  blockers: Array<{
    itemId:    string
    startTime: string
    endTime:   string
    reason:    SpotReplaceBlockerReason
    label:     string
  }>
  resizedCount: number
  /** Même fichier et même durée que le spot : il n'y a rien à remplacer. */
  unchanged: boolean
}

export interface SpotMediaReplaceResult {
  spot:    any
  preview: SpotMediaReplacePreview
  gridIds: string[]
}

// ─── Blocs publicitaires (Ad Groups) ─────────────────────────────────────────

export const infoservicesApi = {
  resolve: (q: { channelId: string }) =>
    get<InfoserviceResolution>(`/infoservices/resolve${qs(q)}`),

  sync: (data: { channelId: string; filePath: string }) =>
    post<InfoserviceResolution>('/infoservices/sync', data),

  fillGaps: (data: { channelId: string; gridId: string; date: string; from?: string; to?: string; minimumGapSec?: number }) =>
    post<InfoserviceFillResult>('/infoservices/fill-gaps', data),
}

export const adGroupsApi = {
  list: (q: { channelId?: string; targetTimeSlot?: string } = {}) =>
    get<any[]>(`/ad-groups${qs(q)}`),

  getById: (id: string) =>
    get<any>(`/ad-groups/${id}`),

  create: (data: { channelId: string; name: string; description?: string; targetTimeSlot?: string }) =>
    post<any>('/ad-groups', data),

  update: (id: string, data: Partial<{ name: string; description: string; targetTimeSlot: string }>) =>
    patch<any>(`/ad-groups/${id}`, data),

  delete: (id: string) =>
    del<void>(`/ad-groups/${id}`),

  addSpots: (groupId: string, spotIds: string[]) =>
    post<any>(`/ad-groups/${groupId}/spots`, { spotIds }),

  removeSpot: (groupId: string, spotId: string) =>
    del<void>(`/ad-groups/${groupId}/spots/${spotId}`),

  reorder: (groupId: string, orderedSpotIds: string[]) =>
    patch<any>(`/ad-groups/${groupId}/reorder`, { orderedSpotIds }),
}

// ─── Placements ──────────────────────────────────────────────────────────────

export const placementsApi = {
  list:     (q: { channelId?: string; date?: string } = {}) => get<any[]>(`/ad-placements${qs(q)}`),
  validate: (id: string)                                     => post<any>(`/ad-placements/${id}/validate`),
  delete:   (id: string)                                     => del<void>(`/ad-placements/${id}`),
}

// ─── Placement par trous horaires ───────────────────────────────────────────

export const adBlocksApi = {
  ranges: (q: { gridId: string; date: string }) =>
    get<{ gridId: string; date: string; ranges: AdBlockRangeStat[] }>(`/ad-blocks/ranges${qs(q)}`),

  customRange: (data: { gridId: string; date: string; startTime: string; endTime: string }) =>
    post<{ gridId: string; date: string; startTime: string; endTime: string; gapCount: number; totalDurationSec: number; gaps: AdBlockGap[] }>('/ad-blocks/ranges/custom', data),

  compose: (data: {
    channelId: string
    gridId: string
    date: string
    gapStart: string
    gapEnd: string
    weekMode?: boolean
    excludedSpotIds?: string[]
  }) => post<AdBlockProposal | { weekMode: true; proposals: AdBlockWeekProposal[] }>('/ad-blocks/compose', data),

  apply: (data: { channelId: string; gridId: string; spots: Array<{ spotId: string; startTime: string; durationSec?: number }> }) =>
    post<{ created: number; items: Array<{ id: string; startTime: string; spotTitle: string }> }>('/ad-blocks/apply', data),

  applyWithInsertion: (data: {
    channelId: string
    gridId: string
    spots: Array<{ spotId: string; startTime: string; durationSec?: number }>
    insertion: AdBlockInsertionSpace
  }) => post<{ created: number; items: Array<{ id: string; startTime: string; spotTitle: string }> }>('/ad-blocks/apply-with-insertion', data),

  applyWeek: (blocks: Array<{ channelId: string; gridId: string; spots: Array<{ spotId: string; startTime: string; durationSec?: number }> }>) =>
    post<{ created: number; days: unknown[] }>('/ad-blocks/apply-week', { blocks }),

  previewAlternating: (data: AlternatingAdsConfig) =>
    post<AlternatingAdsPreview>('/ad-blocks/alternating/preview', data),

  applyAlternating: (data: AlternatingAdsConfig & { selectedTargetKeys: string[] }) =>
    post<{ created: number; items: Array<{ id: string; startTime: string; spotTitle: string }> }>('/ad-blocks/alternating/apply', data),

  /** Récupère le détail d'un bloc pub existant par l'ID du ScheduleItem. */
  getItem: (itemId: string) =>
    get<AdBlockDetail>(`/ad-blocks/items/${itemId}`),

  /** Remplace la liste ordonnée des spots d'un bloc existant. */
  updateItemSpots: (itemId: string, spotIds: string[], realign?: AdBlockRealignSide) =>
    patch<AdBlockDetail>(`/ad-blocks/items/${itemId}/spots`, realign ? { spotIds, realign } : { spotIds }),
}

export const adTransitionApi = {
  get: (channelId: string) =>
    get<AdTransitionSettings>(`/ad-transition-settings/${channelId}`),

  useBlack: (channelId: string, blackDurationMs: number) =>
    patch<AdTransitionSettings>(`/ad-transition-settings/${channelId}`, { mode: 'BLACK', blackDurationMs }),

  useVideo: (channelId: string, filePath?: string) =>
    patch<AdTransitionSettings>(`/ad-transition-settings/${channelId}`, { mode: 'VIDEO', ...(filePath ? { filePath } : {}) }),
}
